import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Card } from '@/components';
import { ExchangeStatusBadge } from '@/components/ExchangeStatusBadge';
import { apiJson } from '@/lib/api';
import { formatDateLocal } from '@/lib/utils';
import type { ExchangeComplianceSummary, HouseholdResponse } from '@/lib/types';
import { getExchangeLogs, type ExchangeStatus } from '@/lib/exchange-api';

function getDateRange(months: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - months);
  return {
    start: formatDateLocal(start),
    end: formatDateLocal(end),
  };
}

function CompliancePage() {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<number | null>(
    null
  );
  const [rangeMonths, setRangeMonths] = useState(3);

  useEffect(() => {
    track('Compliance Screen');
  }, []);

  const { start, end } = useMemo(() => getDateRange(rangeMonths), [rangeMonths]);

  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => apiJson<HouseholdResponse[]>(getToken, '/v1/households'),
    enabled: !!isSignedIn,
  });

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: async () => apiJson<{ id: number }>(getToken, '/v1/user'),
    enabled: !!isSignedIn,
  });

  const firstHousehold = householdsQuery.data?.[0];
  const beneficiaries = useMemo(
    () => firstHousehold?.beneficiaries ?? [],
    [firstHousehold]
  );

  const coParentName = useMemo(() => {
    if (!firstHousehold || !userQuery.data) return 'Co-parent';
    const member = firstHousehold.members?.find(
      (m) => m.user_id !== userQuery.data.id
    );
    return member?.first_name ?? 'Co-parent';
  }, [firstHousehold, userQuery.data]);

  const complianceQuery = useQuery({
    queryKey: ['compliance', start, end, selectedBeneficiaryId],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: start,
        end_date: end,
      });
      if (selectedBeneficiaryId) {
        params.set('beneficiary_id', String(selectedBeneficiaryId));
      }
      return apiJson<ExchangeComplianceSummary>(
        getToken,
        `/v1/exchange-logs/compliance?${params}`
      );
    },
    enabled: !!isSignedIn,
  });

  const allLogsQuery = useQuery({
    queryKey: ['exchange-logs-all', start, end, selectedBeneficiaryId],
    queryFn: async () =>
      getExchangeLogs(getToken, {
        start_date: start,
        end_date: end,
        beneficiary_id: selectedBeneficiaryId ?? undefined,
      }),
    enabled: !!isSignedIn,
  });

  const summary = complianceQuery.data;
  const allLogs = allLogsQuery.data?.exchange_logs ?? [];

  return (
    <div className='min-h-screen bg-sky-light pb-10'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4 flex items-center justify-between gap-4'>
          <Link
            to='/app/calendar'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
          >
            <FiArrowLeft /> Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg mx-auto px-6 py-6 space-y-6'>
        <h1
          className='text-2xl font-light text-primary'
          style={{
            fontFamily:
              "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          Exchange history
        </h1>

        {beneficiaries.length > 1 && (
          <div className='flex gap-2 overflow-x-auto pb-1 -mx-1 px-1'>
            <button
              type='button'
              onClick={() => {
                track('Compliance Beneficiary Filter', { filter: 'all' });
                setSelectedBeneficiaryId(null);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border shrink-0 ${
                !selectedBeneficiaryId
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-gray-100 border-gray-200 text-gray-700'
              }`}
            >
              All
            </button>
            {beneficiaries.map((b) => (
              <button
                key={b.beneficiary_id}
                type='button'
                onClick={() => {
                  track('Compliance Beneficiary Filter', { filter: b.name });
                  setSelectedBeneficiaryId(b.beneficiary_id);
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border shrink-0 ${
                  selectedBeneficiaryId === b.beneficiary_id
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-gray-100 border-gray-200 text-gray-700'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        <div className='flex gap-2 flex-wrap'>
          {[1, 3, 6, 12].map((months) => (
            <button
              key={months}
              type='button'
              onClick={() => {
                track('Compliance Range Changed', { months });
                setRangeMonths(months);
              }}
              className={`px-4 py-2 rounded-full text-sm font-bold ${
                rangeMonths === months
                  ? 'bg-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-700'
              }`}
            >
              {months === 12 ? '1Y' : `${months}M`}
            </button>
          ))}
        </div>

        {complianceQuery.isLoading ? (
          <div className='flex justify-center py-12'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : summary ? (
          <Card className='space-y-4'>
            <div className='text-center py-2'>
              <p className='text-4xl font-bold text-gray-900'>
                {summary.on_time_pct !== null ? `${summary.on_time_pct}%` : '—'}
              </p>
              <p className='text-sm font-semibold text-gray-500 mt-1'>On time</p>
            </div>
            <div className='grid grid-cols-4 gap-2 text-center'>
              <div>
                <p className='text-lg font-bold text-emerald-600'>
                  {summary.by_status.ON_TIME}
                </p>
                <p className='text-[10px] font-semibold text-gray-500 uppercase'>
                  On time
                </p>
              </div>
              <div>
                <p className='text-lg font-bold text-amber-600'>
                  {summary.by_status.LATE}
                </p>
                <p className='text-[10px] font-semibold text-gray-500 uppercase'>Late</p>
              </div>
              <div>
                <p className='text-lg font-bold text-red-600'>
                  {summary.by_status.NO_SHOW}
                </p>
                <p className='text-[10px] font-semibold text-gray-500 uppercase'>
                  No show
                </p>
              </div>
              <div>
                <p className='text-lg font-bold text-gray-500'>
                  {summary.by_status.CANCELLED}
                </p>
                <p className='text-[10px] font-semibold text-gray-500 uppercase'>
                  Cancelled
                </p>
              </div>
            </div>
            <hr className='border-gray-100' />
            <p className='text-sm text-gray-600 text-center'>
              {summary.total_logged} logged exchange
              {summary.total_logged !== 1 ? 's' : ''}
            </p>
            {summary.total_minutes_late !== null && (
              <p className='text-xs text-gray-500 text-center'>
                Total {summary.total_minutes_late} min late · Avg{' '}
                {summary.avg_minutes_late} min late
              </p>
            )}
          </Card>
        ) : (
          <Card className='text-center py-8 text-gray-600'>
            <p className='font-bold text-gray-900'>No exchange data</p>
            <p className='text-sm mt-2'>
              Log exchanges from the calendar to see compliance data here.
            </p>
          </Card>
        )}

        {allLogs.length > 0 && (
          <div>
            <h2 className='text-sm font-bold text-gray-500 uppercase tracking-wide mb-3'>
              Timeline
            </h2>
            <div className='space-y-0'>
              {allLogs.map((log, index) => {
                const benName =
                  beneficiaries.find((b) => b.beneficiary_id === log.beneficiary_id)
                    ?.name ?? 'Child';
                const isLast = index === allLogs.length - 1;
                return (
                  <button
                    key={log.id}
                    type='button'
                    onClick={() => {
                      track('Compliance Timeline Entry Tapped', {
                        status: log.status,
                        exchange_type: log.exchange_type,
                      });
                      navigate({
                        to: '/app/calendar/log-exchange',
                        search: {
                          beneficiary_id: String(log.beneficiary_id),
                          beneficiary_name: benName,
                          scheduled_date: log.scheduled_date,
                          scheduled_time: log.scheduled_time ?? '',
                          exchange_type: log.exchange_type,
                          co_parent_name: coParentName,
                        },
                      });
                    }}
                    className='flex gap-3 w-full text-left py-3 border-b border-gray-100 last:border-0 hover:bg-white/60 rounded-lg px-1 -mx-1'
                  >
                    <div className='flex flex-col items-center w-4 shrink-0 pt-1'>
                      <span
                        className='w-2.5 h-2.5 rounded-full shrink-0'
                        style={{
                          backgroundColor:
                            log.status === 'ON_TIME'
                              ? '#059669'
                              : log.status === 'LATE'
                                ? '#D97706'
                                : log.status === 'NO_SHOW'
                                  ? '#DC2626'
                                  : '#6B7280',
                        }}
                      />
                      {!isLast ? (
                        <span className='w-px flex-1 min-h-[24px] bg-gray-200 mt-1' />
                      ) : null}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between gap-2 flex-wrap'>
                        <span className='text-sm font-semibold text-gray-800'>
                          {new Date(log.scheduled_date + 'T12:00:00').toLocaleDateString(
                            'en-US',
                            {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            }
                          )}
                        </span>
                        <ExchangeStatusBadge
                          status={log.status as ExchangeStatus}
                          minutesLate={log.minutes_late}
                        />
                      </div>
                      <p className='text-sm text-gray-600 mt-1'>
                        {benName} · {log.exchange_type === 'PICKUP' ? 'Pick up' : 'Drop off'}
                      </p>
                      {log.creator_user_name ? (
                        <p className='text-xs text-gray-400 mt-0.5'>
                          Logged by {log.creator_user_name}
                        </p>
                      ) : null}
                      {log.note ? (
                        <p className='text-xs text-gray-500 mt-1 line-clamp-2'>{log.note}</p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Link
          to='/app/settings'
          onClick={() => track('Compliance Export PDF Tapped')}
          className='block w-full text-center px-6 py-3.5 rounded-2xl font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors'
        >
          Email report (Settings → Export)
        </Link>
        <p className='text-xs text-gray-500 text-center -mt-4'>
          Open Settings and enable &quot;Exchange compliance&quot; (and optional date range)
          when you send your export.
        </p>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/compliance')({
  component: CompliancePage,
});
